/**
 * Experience quality negotiation for the world engine.
 *
 * Decides — before three.js is ever downloaded — whether the visitor gets a
 * live WebGL laboratory or the static CSS backdrop, and at which fidelity
 * tier. Tiers only ever step DOWN at runtime (FPS governor), never up.
 */

import type { ExperienceMode } from '../lab/types';

/** Renderable tiers, best → worst. 'static' is reserved for no-WebGL clients. */
export type RenderTier = Exclude<ExperienceMode, 'static'>;

export interface TierSettings {
  /** Device-pixel-ratio clamp handed to the R3F canvas. */
  dpr: [number, number];
  antialias: boolean;
  /** Number of filament curves in the Intelligence Core. */
  filaments: number;
  /** Sample points per filament (segments = points - 1). */
  filamentPoints: number;
  /** Inner knowledge-particle cloud of the core. */
  corePoints: number;
  /** Ambient drifting data particles across the research volume. */
  ambientPoints: number;
  /** Sustained-FPS floor before the governor steps the tier down. */
  fpsFloor: number;
}

export const TIER_SETTINGS: Record<RenderTier, TierSettings> = {
  ultra: {
    dpr: [1, 2],
    antialias: true,
    filaments: 420,
    filamentPoints: 36,
    corePoints: 2600,
    ambientPoints: 6000,
    fpsFloor: 45,
  },
  high: {
    dpr: [1, 1.75],
    antialias: true,
    filaments: 300,
    filamentPoints: 30,
    corePoints: 1800,
    ambientPoints: 3500,
    fpsFloor: 45,
  },
  balanced: {
    dpr: [1, 1.5],
    antialias: false,
    filaments: 200,
    filamentPoints: 24,
    corePoints: 1100,
    ambientPoints: 1800,
    fpsFloor: 45,
  },
  lite: {
    dpr: [1, 1],
    antialias: false,
    filaments: 110,
    filamentPoints: 16,
    corePoints: 520,
    ambientPoints: 700,
    fpsFloor: 24,
  },
};

const STEP_DOWN: Record<ExperienceMode, ExperienceMode> = {
  ultra: 'high',
  high: 'balanced',
  balanced: 'lite',
  // Keep the smallest scene mounted. A slow device gets a quieter 3D scene,
  // never a sudden disappearance of the laboratory background.
  lite: 'lite',
  static: 'static',
};

/** One tier down; the governor bottoms out at 'lite' without unmounting WebGL. */
export function stepDownMode(mode: ExperienceMode): ExperienceMode {
  return STEP_DOWN[mode];
}

export function isRenderTier(mode: ExperienceMode): mode is RenderTier {
  return mode !== 'static';
}

/** Settings for any mode; 'static' maps to the lite budget (transient states). */
export function tierSettings(mode: ExperienceMode): TierSettings {
  return isRenderTier(mode) ? TIER_SETTINGS[mode] : TIER_SETTINGS.lite;
}

export interface QualityDecision {
  mode: ExperienceMode;
  reducedMotion: boolean;
  webglFailed: boolean;
}

function webglAvailable(): boolean {
  try {
    const canvas = document.createElement('canvas');
    const gl =
      canvas.getContext('webgl2') ??
      canvas.getContext('webgl') ??
      canvas.getContext('experimental-webgl');
    return gl !== null;
  } catch {
    return false;
  }
}

function isMobileLike(): boolean {
  const ua = navigator.userAgent;
  const uaMobile = /Android|iPhone|iPad|iPod|Mobile|Silk/i.test(ua);
  const coarse =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches;
  return uaMobile || (coarse && window.innerWidth < 1024);
}

/**
 * Initial quality decision. Runs client-side only, before the three.js
 * chunk is requested. Missing WebGL → 'static'; reduced motion keeps the
 * scene mounted while the world components suppress their movement.
 */
export function detectQuality(): QualityDecision {
  if (typeof window === 'undefined') {
    return { mode: 'static', reducedMotion: false, webglFailed: false };
  }

  const reducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!webglAvailable()) {
    return { mode: 'static', reducedMotion: false, webglFailed: true };
  }

  const nav = navigator as Navigator & { deviceMemory?: number };
  const cores = nav.hardwareConcurrency ?? 4;
  // deviceMemory is Chromium-only; absent (Safari/Firefox) we assume a
  // desktop-class 8 GB rather than punishing capable machines.
  const memory = nav.deviceMemory ?? 8;
  const width = window.innerWidth;

  let mode: ExperienceMode;
  if (isMobileLike()) {
    mode = width >= 820 && cores >= 6 && memory >= 4 ? 'balanced' : 'lite';
  } else if (cores >= 10 && memory >= 8) {
    mode = 'ultra';
  } else if (cores >= 6 && memory >= 4) {
    mode = 'high';
  } else if (cores >= 4) {
    mode = 'balanced';
  } else {
    mode = 'lite';
  }

  return { mode, reducedMotion, webglFailed: false };
}

/* ── Per-visit world seed ────────────────────────────────────────────── */

const SEED_KEY = 'slp-world-seed';

/** Stable within a visit, different across visits. Safe without storage. */
export function getWorldSeed(): number {
  try {
    const stored = window.sessionStorage.getItem(SEED_KEY);
    if (stored !== null) {
      const n = Number.parseInt(stored, 10);
      if (Number.isFinite(n)) return n >>> 0;
    }
    const seed = (Math.random() * 0xffffffff) >>> 0;
    window.sessionStorage.setItem(SEED_KEY, String(seed));
    return seed;
  } catch {
    return 0x9e3779b9;
  }
}

/** Deterministic PRNG (mulberry32) so a seed reproduces one exact world. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
